import {
  getLegacyGradingKey,
  stringifyStructuredContentPhrasingRunAsPlainText,
} from "@repo/app-types/content/structured/helpers";
import type {
  StructuredContentPhrasingNode,
  StructuredContentQuoteSpeaker,
  StructuredContentRoot,
  StructuredContentTable,
  StructuredContentTableAlign,
} from "@repo/app-types/content/structured/types";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { Fragment, type ReactNode } from "react";
import { twJoin } from "tailwind-merge";
import { linkStyle } from "../Link/index.js";
import { CustomerReferenceNode } from "./customerReferenceContext.js";
import { GradingReferenceNode } from "./gradingReferenceContext.js";
import { InteractiveQuote } from "./interactiveQuote.js";
import { InteractiveReferenceBoundary } from "./interactiveReferenceBoundary.js";
import { PlaybookFeedbackReferenceNode } from "./playbookFeedbackReferenceContext.js";
import { TeammateReferenceNode } from "./teammateReferenceContext.js";

/**
 * What the renderer knows about where the content is shown, used to resolve
 * grading references. All fields are fallbacks for content whose nodes don't
 * carry the information themselves: newer quote and interaction nodes
 * store their own `playbookId`, while content stored before that field (and
 * legacy coaching content, which leans on `teammateId`) resolves against the
 * context. Playbook-agnostic content needs no context at all — a reference
 * with no resolvable playbook renders inert.
 */
export interface StructuredContentRenderContext {
  playbookId?: PlaybookId;
  teammateId?: TeammateId;
  criterionId?: string;
  /**
   * Resolves an otherwise-unlinked transcript quote to a host-local action.
   * Returning undefined leaves the quote inert. References that identify a
   * grading continue through GradingReferenceProvider instead.
   */
  getQuoteInteraction?: (
    quote: string,
  ) => StructuredContentQuoteInteraction | undefined;
  /**
   * Names the playbook a teammate reference links into when the reference
   * carries none. Chat surfaces supply it from the thread's tool results.
   */
  resolveTeammatePlaybookId?: (
    teammateId: TeammateId,
  ) => PlaybookId | undefined;
}

export interface StructuredContentQuoteInteraction {
  onPress: () => void;
  /** Optional transcript position displayed as compact metadata in the chip. */
  timestamp?: string;
}

/**
 * The single shared style for rendered structured content: headings render as
 * small semibold labels, paragraphs as relaxed body text.
 *
 * Every top-level block zeroes its top margin when it's the first child
 * (`first:mt-0`) and its bottom margin when it's the last (`last:mb-0`).
 * Leading and trailing vertical rhythm is the container's responsibility, not
 * the block's — and a stray edge margin renders inconsistently depending on
 * whether an ancestor establishes a block formatting context. A collapsed
 * `overflow-hidden` wrapper (see coachingGoalBodyPreview) *contains* the first
 * block's top margin as visible space, while the same content with no such
 * wrapper *collapses* that margin into the surrounding gap — the difference
 * shows up as a layout shift when the content is expanded/collapsed. Likewise,
 * a padded container (e.g. a chat message bubble) contains the last block's
 * bottom margin as awkward extra space above its padding. Keeping the edge
 * blocks flush avoids both, whatever block type happens to sit there.
 */
const headingClassName =
  "mt-4 mb-2 text-sm font-semibold text-foreground first:mt-0 last:mb-0";
const paragraphClassName = "my-2 leading-relaxed first:mt-0 last:mb-0";
const EMPTY_PHRASING_RUN_BOUNDARY: PhrasingRunBoundary = {
  hasContentBefore: false,
  hasContentAfter: false,
};

/**
 * Renders the shared structured content AST (see
 * `app-types/content/structured/types.ts`) to React. Grading references and
 * quotes become interactive when the host app provides a wrapper via
 * `GradingReferenceProvider`; otherwise they render inert.
 */
export function renderStructuredContent(
  node: StructuredContentRoot,
  context: StructuredContentRenderContext,
): ReactNode {
  return node.children.map((child, index) => {
    switch (child.type) {
      case "heading":
        return (
          <p key={index} className={headingClassName}>
            {renderPhrasingRun(child.children, context)}
          </p>
        );

      case "list":
        return (
          <ul key={index} className="my-2 space-y-1.5 first:mt-0 last:mb-0">
            {child.children.map((item, itemIndex) => (
              <li
                key={itemIndex}
                className="flex gap-2"
                style={
                  item.depth
                    ? { paddingLeft: `${item.depth * 1.25}rem` }
                    : undefined
                }
              >
                {item.number === undefined ? (
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-gray-400" />
                ) : (
                  <span className="shrink-0 text-gray-500 tabular-nums">
                    {item.number}.
                  </span>
                )}
                <span>{renderPhrasingRun(item.children, context)}</span>
              </li>
            ))}
          </ul>
        );

      case "paragraph":
        return (
          <p key={index} className={paragraphClassName}>
            {renderPhrasingRun(child.children, context)}
          </p>
        );

      case "table":
        return <Fragment key={index}>{renderTable(child, context)}</Fragment>;

      case "thematicBreak":
        return (
          <hr
            key={index}
            className="my-4 border-0 border-t border-border-light first:mt-0 last:mb-0"
          />
        );

      default:
        return assertUnreachable(child, () => null);
    }
  });
}

function tableCellAlignmentClassName(
  align: StructuredContentTableAlign | undefined,
): string {
  switch (align) {
    case "right":
      return "text-right";
    case "center":
      return "text-center";
    default:
      return "text-left";
  }
}

function renderTable(
  table: StructuredContentTable,
  context: StructuredContentRenderContext,
): ReactNode {
  // The first row is the header row, as in GFM Markdown.
  const [headerRow, ...bodyRows] = table.children;
  if (!headerRow) {
    return null;
  }

  return (
    <div className="my-3 overflow-x-auto first:mt-0 last:mb-0">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {headerRow.children.map((cell, cellIndex) => (
              <th
                key={cellIndex}
                className={twJoin(
                  "border-b border-gray-200 px-3 py-1.5 text-xs font-semibold text-muted-foreground",
                  tableCellAlignmentClassName(table.align?.[cellIndex]),
                )}
              >
                {renderPhrasingRun(cell.children, context)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.children.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={twJoin(
                    "border-b border-gray-100 px-3 py-1.5 align-top",
                    tableCellAlignmentClassName(table.align?.[cellIndex]),
                  )}
                >
                  {renderPhrasingRun(cell.children, context)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderPhrasingRun(
  nodes: readonly StructuredContentPhrasingNode[],
  context: StructuredContentRenderContext,
  boundary: PhrasingRunBoundary = EMPTY_PHRASING_RUN_BOUNDARY,
): ReactNode {
  let firstRenderableNodeIndex = -1;
  let lastRenderableNodeIndex = -1;

  nodes.forEach((node, index) => {
    if (!hasRenderablePhrasingContent(node)) {
      return;
    }

    if (firstRenderableNodeIndex === -1) {
      firstRenderableNodeIndex = index;
    }
    lastRenderableNodeIndex = index;
  });

  return nodes.map((node, index) => {
    const position = {
      hasContentBefore:
        boundary.hasContentBefore ||
        (firstRenderableNodeIndex !== -1 && firstRenderableNodeIndex < index),
      hasContentAfter:
        boundary.hasContentAfter || lastRenderableNodeIndex > index,
    };
    return (
      <Fragment key={index}>
        {renderPhrasingNode(node, context, position)}
      </Fragment>
    );
  });
}

function hasRenderablePhrasingContent(
  node: StructuredContentPhrasingNode,
): boolean {
  if (node.type === "text") {
    return node.value.trim().length > 0;
  }

  return node.children.some(hasRenderablePhrasingContent);
}

interface PhrasingRunBoundary {
  /** Content exists before this run/node, including outside inline formatting. */
  hasContentBefore: boolean;
  /** Content exists after this run/node, including outside inline formatting. */
  hasContentAfter: boolean;
}

/**
 * The phrasing nodes that can link to a grading: an inline interaction (or
 * legacy customer) link and a transcript `quote`. All resolve the reference
 * target the same way and differ only in their visual treatment.
 */
type LinkedGradingReferenceNode = Extract<
  StructuredContentPhrasingNode,
  { type: "grading_reference" | "interaction_reference" | "quote" }
>;

/**
 * Renders a potentially clickable grading reference (used by the interaction,
 * legacy customer, and linked `quote` branches). The reference target is
 * built once here so the resolution rules can't drift between the node
 * types; callers vary only the wrapper's `className`.
 */
function renderLinkedGradingReference(
  node: LinkedGradingReferenceNode,
  context: StructuredContentRenderContext,
  className: string,
): ReactNode {
  // Newer nodes carry their own playbook (content may span playbooks); older
  // content falls back to the playbook of the rendering context. Without
  // either there is nothing to link to, so render the reference inert.
  const playbookId = node.playbookId ?? context.playbookId;
  if (!playbookId) {
    return (
      <span className={className}>
        {renderPhrasingRun(node.children, context)}
      </span>
    );
  }

  // A quote points at the exact line in the call, so it links to the transcript
  // tab and deep-links to the fragment via its verbatim text (like the research
  // agent's quote links). A plain grading reference keeps pointing at the
  // assessment (criterion) view.
  const isQuote = node.type === "quote";
  const legacyKey = node.gradingId ? undefined : getLegacyGradingKey(node);

  return (
    <GradingReferenceNode
      reference={{
        gradingId: node.gradingId,
        // When the grading is identified by id, resolve teammate/account from
        // the grading itself rather than falling back to the contextual
        // teammate, because the reference may belong to a different rep.
        teammateId: node.gradingId
          ? undefined
          : (legacyKey?.teammateId ?? context.teammateId),
        accountId: legacyKey?.accountId,
        playbookId,
        target: isQuote ? "transcript" : undefined,
        // The criterion pins a grading reference to its assessment row; a quote
        // instead lands on the transcript, so it carries no criterion.
        criterionId: isQuote ? undefined : context.criterionId,
        quote: isQuote
          ? stringifyStructuredContentPhrasingRunAsPlainText(node.children)
          : undefined,
      }}
      className={className}
    >
      {renderPhrasingRun(node.children, context)}
    </GradingReferenceNode>
  );
}

function renderPhrasingNode(
  content: StructuredContentPhrasingNode,
  context: StructuredContentRenderContext,
  position: PhrasingRunBoundary,
): ReactNode {
  switch (content.type) {
    case "text":
      return content.value;

    case "emphasis":
      return <em>{renderPhrasingRun(content.children, context, position)}</em>;

    case "strong":
      return (
        <strong className="font-semibold">
          {renderPhrasingRun(content.children, context, position)}
        </strong>
      );

    case "highlight":
      return (
        <span
          // Can't use twMerge here because it will consider text-highlight and text-highlight-green/red as conflicting classes.
          className={twJoin(
            "text-highlight",
            content.color === "green" ? "text-highlight-green" : "",
            content.color === "red" ? "text-highlight-red" : "",
          )}
        >
          {renderPhrasingRun(content.children, context, position)}
        </span>
      );

    case "interaction_reference":
      return renderLinkedGradingReference(
        content,
        context,
        entityReferenceClassName,
      );

    case "grading_reference": {
      // Without a grading id or account, there's nothing to link to. Render the
      // reference styled but inert (no link affordance), like an unlinked quote.
      if (!content.gradingId && !content.accountId) {
        return (
          <span className={entityReferenceClassName}>
            {renderPhrasingRun(content.children, context)}
          </span>
        );
      }

      return renderLinkedGradingReference(
        content,
        context,
        entityReferenceClassName,
      );
    }

    case "customer_reference":
      return (
        <CustomerReferenceNode className={entityReferenceClassName}>
          {renderPhrasingRun(content.children, context, position)}
        </CustomerReferenceNode>
      );

    case "teammate_group_reference":
    case "person_reference":
    case "date_reference":
      return renderPhrasingRun(content.children, context, position);

    case "playbook_feedback_reference":
      return (
        <PlaybookFeedbackReferenceNode
          reference={{
            playbookId: content.playbookId,
            nodeId: content.nodeId,
            feedbackId: content.feedbackId,
          }}
          className={linkStyle()}
        >
          {renderPhrasingRun(content.children, context, position)}
        </PlaybookFeedbackReferenceNode>
      );

    case "teammate_reference":
      return (
        <TeammateReferenceNode
          reference={{
            teammateId: content.teammateId,
            playbookId:
              content.playbookId ??
              context.resolveTeammatePlaybookId?.(content.teammateId),
          }}
          className={entityReferenceClassName}
        >
          {renderPhrasingRun(content.children, context, position)}
        </TeammateReferenceNode>
      );

    case "quote": {
      const chip = quoteChipClassNames(content.speaker);
      let quoteNode: ReactNode;

      // A host that already has the relevant transcript open can make an
      // unqualified quote interactive without turning it into an external
      // grading reference.
      if (!content.gradingId && !content.accountId) {
        const quote = stringifyStructuredContentPhrasingRunAsPlainText(
          content.children,
        );
        const interaction = context.getQuoteInteraction?.(quote);
        if (interaction) {
          quoteNode = (
            <InteractiveQuote
              className={chip.className}
              metadataClassName={chip.metadata}
              onPress={interaction.onPress}
              quote={quote}
              timestamp={interaction.timestamp}
            >
              {/* The quote button owns the interaction for its subtree. */}
              <InteractiveReferenceBoundary>
                {renderPhrasingRun(content.children, context)}
              </InteractiveReferenceBoundary>
            </InteractiveQuote>
          );
        } else {
          quoteNode = (
            <span className={chip.className}>
              {renderPhrasingRun(content.children, context)}
            </span>
          );
        }
      } else {
        quoteNode = renderLinkedGradingReference(
          content,
          context,
          chip.className,
        );
      }

      // The inner chip clones compensated padding onto every wrapped fragment.
      // Normal whitespace restores spacing at logical prose boundaries while
      // still collapsing at a visual line edge. Fixed wrapper padding cannot do
      // that: when layout pushes a logically inline quote onto the next line,
      // it indents only the first fragment and misaligns its continuations.
      return (
        <span className="box-decoration-slice">
          {position.hasContentBefore ? " " : null}
          {quoteNode}
          {position.hasContentAfter ? " " : null}
        </span>
      );
    }

    default:
      return assertUnreachable(content, () => null);
  }
}

/** Shared styling for customer and teammate references. */
const entityReferenceClassName =
  "font-semibold text-blue-800 [&[href]:hover]:text-blue-900";

/**
 * Quote chips are colored by speaker: customer quotes amber, rep/agent quotes
 * (and quotes without a speaker) blue. Borderless with a color transition; the
 * `interactive` variants deepen the chip on hover when it links out to the
 * grading or the host makes it a local quote button.
 */
const quoteChipPalettes: Record<
  StructuredContentQuoteSpeaker,
  { base: string; interactive: string; metadata: string }
> = {
  customer: {
    base: "bg-amber-50 text-amber-900",
    interactive:
      "[&[href]:hover]:bg-amber-200 [&[href]:hover]:text-amber-950 [&[role=button]:hover]:bg-amber-200 [&[role=button]:hover]:text-amber-950",
    metadata: "text-amber-900/80",
  },
  agent: {
    base: "bg-blue-50 text-blue-800",
    interactive:
      "[&[href]:hover]:bg-blue-200 [&[href]:hover]:text-blue-900 [&[role=button]:hover]:bg-blue-200 [&[role=button]:hover]:text-blue-900",
    metadata: "text-blue-800/80",
  },
};

function quoteChipClassNames(
  speaker: StructuredContentQuoteSpeaker | undefined,
): { className: string; metadata: string } {
  const palette = quoteChipPalettes[speaker ?? "agent"];
  const chip =
    "box-decoration-clone -mx-1 rounded-md px-1 py-0.5 font-mono text-xs transition-colors";
  return {
    className: twJoin(chip, palette.base, palette.interactive),
    metadata: palette.metadata,
  };
}
